// seoShared.js
//
// SINGLE SOURCE OF TRUTH for per-page SEO metadata (title / description /
// canonical / hreflang / JSON-LD building blocks).
//
// This file is plain CommonJS-compatible JS (no TypeScript syntax) so it can
// be `require()`-d directly, unmodified, from two very different runtimes:
//
//   1. scripts/build-seo.js   (plain Node, runs after `react-scripts build`)
//   2. src/services/seo.ts    (a thin typed wrapper imported by React
//                              components for react-helmet-async)
//
// IMPORTANT: keep this the ONLY place that computes title/description
// strings. If the build script and the client ever compute different
// text for the same route, the static HTML and the post-hydration DOM
// will disagree — which is exactly the bug this whole SEO phase exists to
// fix. Do not duplicate this logic anywhere else.

const SITE_URL = 'https://avocatkovacstimea.ro';

const LANGS = ['ro', 'hu', 'en'];

const LOGO_PATH = '/LogoKT.png';

const OG_LOCALE = { ro: 'ro_RO', hu: 'hu_HU', en: 'en_US' };

const BUSINESS = {
  name: 'Cabinet de Avocat Kovács Tímea',
  legalName: 'Cabinet de Avocat Kovács Tímea',
  streetAddress: 'Str. Unirii nr. 13, et. 1',
  addressLocality: 'Zalău',
  addressRegion: 'Sălaj',
  addressCountry: 'RO',
  telephone: '+40740245886',
  email: 'avocat.kt@gmail.com',
  areaServed: 'Sălaj',
  availableLanguages: ['ro', 'hu', 'en'],
  sameAs: [
    'https://maps.app.goo.gl/GM9xsjFrpXebxFZs9',
    'https://www.facebook.com/profile.php?id=61571397660008',
    'https://www.linkedin.com/in/kovacs-timea-638838170',
    'https://cautavocat.ro/avocat/kovacs-timea-1',
  ],
  founder: {
    name: 'Kovács Tímea',
    description: {
      ro: 'Avocat, membră a Baroului Sălaj din anul 2013.',
      hu: 'Ügyvéd, a Szilágy Megyei Ügyvédi Kamara tagja 2013 óta.',
      en: 'Attorney, member of the Sălaj Bar Association since 2013.',
    },
    // The CautAvocat directory profile is public, third-party corroboration
    // of Baroul Sălaj membership — a genuine credibility signal, so it
    // belongs on the Person node (author of articles) as well as on the
    // LegalService node above.
    sameAs: ['https://cautavocat.ro/avocat/kovacs-timea-1'],
  },
};

/**
 * Truncates `str` to at most `max` characters without cutting a word in
 * half. Falls back to a hard cut only if a single "word" already exceeds
 * `max` (should not happen with real sentence copy).
 */
function truncateAtWord(str, max) {
  const clean = str.trim().replace(/\s+/g, ' ');
  if (clean.length <= max) return clean;
  const slice = clean.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace <= 0) return slice;
  return slice.slice(0, lastSpace).trim();
}

/**
 * Best-effort fit of `str` into the [min, max] character window used for
 * meta descriptions (150-160 chars is the SEO convention). Since we are not
 * allowed to invent new copy, this only truncates — it never pads with
 * fabricated sentences. If the source copy is shorter than `min` the
 * result stays shorter than `min`; callers may combine multiple existing
 * locale strings before calling this to get closer to the target window.
 */
function fitDescription(str, min = 150, max = 160) {
  return truncateAtWord(str, max);
}

/** Joins non-empty strings with " " after trimming, dropping empties. */
function joinNonEmpty(parts, sep = ' ') {
  return parts.filter((p) => p && p.trim().length > 0).join(sep);
}

function absoluteUrl(pathname) {
  if (pathname === '' || pathname === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${pathname}`;
}

/** Canonical path for the homepage of a given language. */
function homePath(lang) {
  return `/${lang}`;
}

function articlesListPath(lang) {
  return `/${lang}/articles`;
}

function articlePath(lang, slug) {
  return `/${lang}/articles/${slug}`;
}

function privacyPath(lang) {
  return `/${lang}/privacy-policy`;
}

/**
 * Builds { title, description } for the homepage in a given language.
 * `t` is that language's full locale object (src/locales/{lang}.json).
 */
function getHomeMeta(lang, t) {
  const primary = t.hero.eyebrow; // e.g. "Avocat în Zalău, Sălaj"
  const brand = t.page.title; // e.g. "Cabinet de Avocat Kovács Tímea"
  const title = truncateAtWord(`${primary} | ${brand}`, 60);
  const description = fitDescription(t.hero.description);
  return { title, description };
}

function getArticlesListMeta(lang, t) {
  const title = truncateAtWord(`${t.articles.title} | ${t.page.title}`, 60);
  const description = fitDescription(
    joinNonEmpty([t.articles.description, t.hero.eyebrow + '.'])
  );
  return { title, description };
}

function getArticleMeta(lang, article, t) {
  const practiceLabel =
    (t.articlePage.practiceAreas && t.articlePage.practiceAreas[article.practiceArea]) ||
    article.practiceArea;
  // NEVER truncate an article headline. Search engines shorten what they
  // DISPLAY, but they index the whole tag — cutting it here threw away the
  // article's primary keyword ("…pentru plata unei" lost "sulte"), which is
  // exactly the term the page is meant to rank for. An article may set an
  // explicit `seoTitle` in its frontmatter when the headline is long and a
  // tighter one would read better in results; otherwise the full headline
  // stands, with the practice area appended only when it genuinely fits.
  let title = article.seoTitle || article.title;
  if (!article.seoTitle) {
    const withArea = `${title} | ${practiceLabel}`;
    if (withArea.length <= 60) title = withArea;
  }
  const description = fitDescription(article.description);
  return { title, description, practiceLabel };
}

function getPrivacyMeta(lang, t) {
  const title = truncateAtWord(`${t.page.footer.privacy.title} | ${t.page.title}`, 60);
  const description = fitDescription(t.page.footer.privacy.dataCollection.content);
  return { title, description };
}

/** Normalises an article's frontmatter "date" (YYYY-MM or YYYY-MM-DD) to ISO 8601. */
function toIsoDate(date) {
  if (/^\d{4}-\d{2}$/.test(date)) return `${date}-01`;
  return date;
}

/** LegalService JSON-LD for the homepage. */
function buildLegalServiceSchema(lang) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    name: BUSINESS.name,
    image: absoluteUrl(LOGO_PATH),
    url: absoluteUrl(homePath(lang)),
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: BUSINESS.streetAddress,
      addressLocality: BUSINESS.addressLocality,
      addressRegion: BUSINESS.addressRegion,
      addressCountry: BUSINESS.addressCountry,
    },
    areaServed: BUSINESS.areaServed,
    availableLanguage: BUSINESS.availableLanguages,
    sameAs: BUSINESS.sameAs,
  };
}

/** FAQPage JSON-LD built from a language's faq.items (question + answer). */
function buildFaqPageSchema(lang, t) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: t.faq.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/** Article + Person(author) JSON-LD for an article page. */
function buildArticleSchema(lang, article, practiceLabel) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: toIsoDate(article.date),
    inLanguage: lang,
    articleSection: practiceLabel,
    author: {
      '@type': 'Person',
      name: BUSINESS.founder.name,
      description: BUSINESS.founder.description[lang] || BUSINESS.founder.description.ro,
      sameAs: BUSINESS.founder.sameAs,
    },
    publisher: {
      '@type': 'LegalService',
      name: BUSINESS.name,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl(LOGO_PATH),
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl(articlePath(lang, article.slug)),
    },
  };
}

/** BreadcrumbList JSON-LD. `crumbs` is an ordered array of { name, path }. */
function buildBreadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

module.exports = {
  SITE_URL,
  LANGS,
  LOGO_PATH,
  OG_LOCALE,
  BUSINESS,
  truncateAtWord,
  fitDescription,
  joinNonEmpty,
  absoluteUrl,
  homePath,
  articlesListPath,
  articlePath,
  privacyPath,
  getHomeMeta,
  getArticlesListMeta,
  getArticleMeta,
  getPrivacyMeta,
  toIsoDate,
  buildLegalServiceSchema,
  buildFaqPageSchema,
  buildArticleSchema,
  buildBreadcrumbSchema,
};
